import {initials, nameFromEmail} from '../lib/aggregate';
import {Role} from '../lib/api';

export type PageKey = 'grants' | 'requests' | 'users' | 'claude-runs';

interface Props {
    active: PageKey;
    onNavigate: (key: PageKey) => void;
    email: string;
    role: Role;
    onSignOut: () => void;
    onRequestAccess: () => void;
}

interface Item {
    key: PageKey;
    label: string;
    icon: JSX.Element;
    adminOnly?: boolean;
}

const ITEMS: Item[] = [
    {
        key: 'grants',
        label: 'Grants',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" aria-hidden>
                <path d="M4 5h16M4 12h16M4 19h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        key: 'requests',
        label: 'Requests',
        icon: (
            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        key: 'users',
        label: 'Users',
        adminOnly: true,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" aria-hidden>
                <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
                <path
                    d="M3 20c0-3.31 2.69-6 6-6s6 2.69 6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
                <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
                <path
                    d="M14 19c.6-2.3 2.4-4 5-4s4.4 1.7 5 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
            </svg>
        ),
    },
    {
        key: 'claude-runs',
        label: 'Claude runs',
        adminOnly: true,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" aria-hidden>
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M7 10l3 2-3 2M12 14h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
];

/**
 * Left rail: brand, the "Request access" CTA, then nav, then the current user
 * + sign-out tucked into the footer. The Users and Claude runs nav items only render
 * for admins. Nav state lives in the URL hash (`#/grants`, `#/requests`, `#/users`,
 * `#/claude-runs`).
 */
export function Sidebar({active, onNavigate, email, role, onSignOut, onRequestAccess}: Props) {
    const name = nameFromEmail(email);
    const items = ITEMS.filter(it => !it.adminOnly || role === 'admin');

    return (
        <aside className="sidebar" aria-label="Primary navigation">
            <div className="sidebar-brand">
                <img src="/logos/ennabl-mark.svg" alt="" width={24} height={24} aria-hidden />
                <span>AccessOps</span>
            </div>

            {/* Same CTA wired for both roles; the dialog itself renders the
                appropriate flow (admin → user picker + direct grant, user →
                self-request). */}
            <button type="button" className="sidebar-cta" onClick={onRequestAccess}>
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16" aria-hidden>
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{role === 'admin' ? 'Grant access' : 'Request access'}</span>
            </button>

            <nav className="sidebar-nav">
                {items.map(it => (
                    <button
                        key={it.key}
                        type="button"
                        className={`sidebar-item${active === it.key ? ' active' : ''}`}
                        onClick={() => onNavigate(it.key)}
                    >
                        {it.icon}
                        <span>{it.label}</span>
                    </button>
                ))}
            </nav>

            <div className="sidebar-foot">
                <div className="sidebar-user">
                    <span className="avatar" aria-hidden>
                        {initials(name, email)}
                    </span>
                    <div className="sidebar-user-meta">
                        <div className="sidebar-user-name">{name || email}</div>
                        <div className="sidebar-user-email">{email}</div>
                    </div>
                </div>
                <button type="button" className="sidebar-signout" onClick={onSignOut}>
                    Sign out
                </button>
            </div>
        </aside>
    );
}

export function readPageFromHash(): PageKey {
    const h = window.location.hash.replace(/^#\/?/, '');
    if (h === 'requests') {
        return 'requests';
    }
    if (h === 'users') {
        return 'users';
    }
    if (h === 'claude-runs') {
        return 'claude-runs';
    }
    return 'grants';
}

export function writePageToHash(page: PageKey) {
    const next = `#/${page}`;
    if (window.location.hash !== next) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${next}`);
    }
}
